import { useContext, useEffect, useState } from 'react';
import { WAS_BACK_END_API_URL, URL_VERSION, TAS_BACK_END_API_URL, URL_TAS_VERSION } from '../../constants';
import { fetchAuthenticated } from '..';
import { PatientRecord, PatientsAjaxResult } from '../../shared/types/types';
import { decryptObject } from '../../components/Encryption/obfuscationHandler';
import useSWR from 'swr';
import { BannerMessageContext } from '../../shared/contexts/BannerMessageContext';
import { v4 } from 'uuid';

export interface PatientDataFetchProps {
  patientId: string;
  token?: string;
}

export interface RefreshOptionProps {
  showArchived: boolean;
}
export interface PatientDataFetchReturnProps {
  data?: PatientRecord;
  loading: boolean;
  startRefresh: (props: RefreshOptionProps) => {};
  error: any;
}

export const usePatientInfoFetcher = ({ patientId, token }: PatientDataFetchProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<PatientRecord>();
  const [error, setError] = useState<any>(null);

  // To refresh this, call the returned startRefresh function with the object {showArchived:true/false}
  const [refresh, startRefresh] = useState<RefreshOptionProps>({
    showArchived: true,
  });

  useEffect(() => {
    const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/patients/${patientId}?archived=${refresh.showArchived ?? true}`;
    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const getInfusionInfo = async () => {
      setLoading(true);
      try {
        // get the patient data from the backend
        const response = await fetchAuthenticated<PatientsAjaxResult>(url, config);

        if (response?.ok) {
          const apiData = response.parsedBody;

          // now decrypt the data
          if (apiData?.result?.patients && apiData?.result?.patients.length > 0) {
            try {
              let decryptedData = { ...apiData.result.patients[0] };
              await decryptObject(decryptedData, token, null);
              setData(decryptedData);
            } catch (error) {
              setError(`An error occurred getting patient data from the server: ${error}`);
              setData(undefined);
            }
          } else {
            setData(undefined);
          }
        }
        setLoading(false);
      } catch (e: any) {
        setError(`An error occurred getting patient data from the server: ${error}`);
      }
    };

    getInfusionInfo();
  }, [refresh, patientId, token, error]);

  return { data, loading, startRefresh, error } as const;
};

export const useInfusionList = (token: string | null) => {
  const url = `${TAS_BACK_END_API_URL}${URL_TAS_VERSION}/treatablepatients`;
  const { addBannerMessage } = useContext(BannerMessageContext);

  const fetchWithToken = async (url: string, token: string) => {
    const config: RequestInit = {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    const response = await fetchAuthenticated<any>(url, config);

    if (response?.ok) {
      // decrypt patient data
      const apiData = response.parsedBody;

      let decryptedData;

      if (
        (apiData && apiData?.result && apiData?.result?.cas_data && apiData?.result?.cas_data.length > 0) ||
        (apiData?.result?.tds_data && apiData?.result?.tds_data.length > 0)
      ) {
        try {
          decryptedData = apiData.result;
          await decryptObject(decryptedData, token, null);
          let patientList: any = [];
          let cas_data = decryptedData?.cas_data || [];
          let tds_data = decryptedData?.tds_data || [];

          for (let i = 0; i < cas_data.length; i++) patientList.push(cas_data[i].patientDoc);
          for (let i = 0; i < tds_data.length; i++) patientList.push(tds_data[i].patientDoc);

          return patientList;
        } catch (e: any) {
          const error = new Error('An error occurred getting patient data from the server: ', e);
          addBannerMessage({
            id: v4(),
            message: 'Uh oh! An error occurred downloading the patient list!',
            type: 'error',
          });
          throw error;
        }
      }

      return decryptedData;
    }
  };

  const { data, error, isLoading } = useSWR([url, token], ([_url, _token]) => fetchWithToken(_url, _token ?? ''), { shouldRetryOnError: false });

  return {
    patientListData: data,
    patientListDataError: error,
    patientListDataIsLoading: isLoading,
  };
};
